import { useState, useEffect, useCallback } from "react";
import {
  assignClassScheduleAPI,
  assignClassScheduleByStudentAPI,
  deleteAssignClassScheduleAPI,
} from "../../services/portal/admin/studentClassScheduleService";
import {
  getAdminClassesAPI,
  getClassScheduleByClassAPI,
} from "../../services/portal/admin/classService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../utility/ToastMessage";
import RemoveConfirmationDialog from "./RemoveConfirmationDialog";
import Loader from "../Loader";
import { formatDate } from "../../utility/Common";

const StudentAssignClassScheduleManage = ({ student }) => {
  const [classes, setClasses] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [formData, setFormData] = useState({
    classId: "",
    classScheduleId: "",
    endDate: "",
  });

  const loadClasses = async () => {
    try {
      const res = await getAdminClassesAPI();
      if (res && res.IsSuccessful && res.Data) {
        setClasses(res.Data);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
    }
  };

  const loadAssignments = useCallback(async () => {
    if (!student?.UserId) return;
    setLoadingAssignments(true);
    try {
      const res = await assignClassScheduleByStudentAPI(student.UserId);
      if (res && res.IsSuccessful && res.Data) {
        setAssignments(res.Data);
      } else {
        setAssignments([]);
      }
    } catch (error) {
      console.error("Error loading assignments:", error);
      showErrorToastMsg("Failed to load assigned schedules");
      setAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  }, [student?.UserId]);

  useEffect(() => {
    loadClasses();
  }, []);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const handleClassChange = async (e) => {
    const classId = e.target.value;
    setFormData({ ...formData, classId, classScheduleId: "" });
    setSchedules([]);
    if (!classId) return;
    try {
      const res = await getClassScheduleByClassAPI(classId);
      if (res && res.IsSuccessful && res.Data) {
        setSchedules(res.Data);
      }
    } catch (error) {
      console.error("Error loading schedules:", error);
      showErrorToastMsg("Failed to load schedules");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.classId) {
      showErrorToastMsg("Please select a class");
      return;
    }
    if (!formData.classScheduleId) {
      showErrorToastMsg("Please select a schedule");
      return;
    }
    if (!formData.endDate) {
      showErrorToastMsg("Please select an end date");
      return;
    }

    setSaving(true);
    try {
      const params = {
        id: 0,
        userId: Number(student.UserId),
        classId: Number(formData.classId),
        classScheduleId: Number(formData.classScheduleId),
        date: formData.endDate,
      };
      const res = await assignClassScheduleAPI(params);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg(res.Message || "Schedule assigned successfully");
        setFormData({ classId: "", classScheduleId: "", endDate: "" });
        setSchedules([]);
        loadAssignments();
      } else {
        showErrorToastMsg(res?.Message || "Failed to assign schedule");
      }
    } catch (error) {
      console.error("Error assigning schedule:", error);
      showErrorToastMsg("Failed to assign schedule");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (assignment) => {
    setDeleteTarget(assignment);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteAssignClassScheduleAPI(deleteTarget.Id);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg(res.Message || "Assignment deleted successfully");
        setDeleteTarget(null);
        loadAssignments();
      } else {
        showErrorToastMsg(res?.Message || "Failed to delete assignment");
      }
    } catch (error) {
      console.error("Error deleting assignment:", error);
      showErrorToastMsg("Failed to delete assignment");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {/* Form Section */}
      <div className="bg-white border border-gray-200 rounded-lg mb-6">
        <div className="bg-gradient-to-br from-black to-amber-500 px-6 py-4 rounded-t-lg">
          <h4 className="text-md font-medium text-white">
            Assign Class Schedule
          </h4>
        </div>
        <form onSubmit={handleSave} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Class <span className="text-red-500">*</span>
              </label>
              <select
                name="classId"
                value={formData.classId}
                onChange={handleClassChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">-- Select Class --</option>
                {classes.map((cls) => (
                  <option key={cls.ClassId} value={cls.ClassId}>
                    {cls.Name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Schedule <span className="text-red-500">*</span>
              </label>
              <select
                name="classScheduleId"
                value={formData.classScheduleId}
                onChange={handleChange}
                disabled={!formData.classId}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Schedule --</option>
                {schedules.map((sch) => (
                  <option key={sch.ClassScheduleId} value={sch.ClassScheduleId}>
                    {formatDate(sch.StartDate)} - {sch.Description}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 font-medium"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>

      {/* Assigned Schedules Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gradient-to-br from-black to-amber-500 px-6 py-4">
          <h4 className="text-md font-medium text-white">
            Assigned Schedules
          </h4>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Class
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Schedule
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expiry Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loadingAssignments ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center">
                    <Loader />
                  </td>
                </tr>
              ) : assignments.length === 0 ? (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-4 text-center text-sm text-gray-500"
                  >
                    No schedules assigned.
                  </td>
                </tr>
              ) : (
                assignments.map((assignment) => (
                  <tr key={assignment.Id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {assignment.ClassName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(assignment.ClassSchedule?.StartDate) + " ( " + assignment.ClassSchedule?.Description + " )"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(assignment.Date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleDeleteClick(assignment)}
                        className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium text-xs"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {deleteTarget && (
        <RemoveConfirmationDialog
          loading={deleting}
          title="Delete Assignment"
          mainDescription="Are you sure you want to delete this class schedule assignment?"
          description={`${deleteTarget.ClassSchedule?.Description}`}
          cancelDelete={() => setDeleteTarget(null)}
          confirmDelete={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default StudentAssignClassScheduleManage;
